export const READING_TIME_MIN_SLICE_MS = 1_000;
export const READING_TIME_MAX_SLICE_MS = 30_000;
export const READING_TIME_OUTBOX_KEY = "manga-reader-reading-time-outbox-v1";
export const READING_TIME_OUTBOX_MAX_EVENTS = 2_000;

export interface ReadingTimeEvent {
  eventId: string;
  pageId?: string | null;
  startedAt: string;
  endedAt: string;
  activeMs: number;
}

export interface QueuedReadingTimeEvent extends ReadingTimeEvent {
  bookId: string;
}

export interface ReadingTimeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ReadingTimeClock {
  monotonicNow(): number;
  wallNow(): number;
}

export type ReadingTimeEventIdFactory = () => string;

export interface ReadingTimeSessionOptions {
  clock?: ReadingTimeClock;
  eventIdFactory?: ReadingTimeEventIdFactory;
  minSliceMs?: number;
  maxSliceMs?: number;
  pageId?: string | null;
}

export interface ReadingTimeOutboxOptions {
  storage?: ReadingTimeStorage | null;
  storageKey?: string;
  maxEvents?: number;
}

export interface SendReadingTimeOptions {
  signal?: AbortSignal;
  keepalive: boolean;
}

export type SendReadingTimeEvent = (
  bookId: string,
  event: ReadingTimeEvent,
  options: SendReadingTimeOptions,
) => Promise<void>;

export interface DrainReadingTimeOutboxOptions {
  signal?: AbortSignal;
  keepalive?: boolean;
  maxEvents?: number;
}

export interface ReadingTimeOutboxDrainResult {
  attempted: number;
  sent: number;
  remaining: number;
  failed: boolean;
}

export interface ReadingTimeRecorderOptions extends ReadingTimeSessionOptions,
  ReadingTimeOutboxOptions {
  outbox?: ReadingTimeOutbox;
}

interface StoredReadingTimeOutbox {
  version: 1;
  events: QueuedReadingTimeEvent[];
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

const defaultClock: ReadingTimeClock = {
  monotonicNow: () => globalThis.performance?.now?.() ?? Date.now(),
  wallNow: () => Date.now(),
};

function positiveInteger(value: number, name: string): number {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer.`);
  }
  return value;
}

function safeClockValue(value: number, name: string): number {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must return a finite number.`);
  }
  return value;
}

/**
 * Generates a canonical UUID even when randomUUID is unavailable, such as on
 * an HTTP LAN origin where Web Crypto's secure-context APIs may be restricted.
 */
export function createReadingTimeEventId(): string {
  const cryptoObject = globalThis.crypto;
  if (typeof cryptoObject?.randomUUID === "function") {
    return cryptoObject.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof cryptoObject?.getRandomValues === "function") {
    cryptoObject.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
}

function splitDuration(durationMs: number, minSliceMs: number, maxSliceMs: number): number[] {
  if (durationMs < minSliceMs) return [];

  const chunks: number[] = [];
  let remainingMs = durationMs;
  while (remainingMs > maxSliceMs) {
    chunks.push(maxSliceMs);
    remainingMs -= maxSliceMs;
  }
  if (remainingMs > 0) chunks.push(remainingMs);

  const finalIndex = chunks.length - 1;
  if (finalIndex > 0 && chunks[finalIndex]! < minSliceMs) {
    const amountNeeded = minSliceMs - chunks[finalIndex]!;
    chunks[finalIndex - 1] = chunks[finalIndex - 1]! - amountNeeded;
    chunks[finalIndex] = minSliceMs;
  }

  return chunks;
}

/**
 * Tracks one volume's continuous visible-reading intervals. The owner decides
 * when reading is active; no browser state is inferred here. Calls are
 * idempotent so React StrictMode setup/cleanup does not double-count a slice.
 */
export class ReadingTimeSession {
  readonly bookId: string;
  private readonly clock: ReadingTimeClock;
  private readonly eventIdFactory: ReadingTimeEventIdFactory;
  private readonly minSliceMs: number;
  private readonly maxSliceMs: number;
  private activeStartedAtMonotonic: number | null = null;
  private activeStartedAtWall: number | null = null;
  private pageId: string | null;

  constructor(bookId: string, options: ReadingTimeSessionOptions = {}) {
    if (bookId.trim().length === 0) {
      throw new TypeError("Reading time requires a book id.");
    }

    this.bookId = bookId;
    this.pageId = options.pageId ?? null;
    this.clock = options.clock ?? defaultClock;
    this.eventIdFactory = options.eventIdFactory ?? createReadingTimeEventId;
    this.minSliceMs = positiveInteger(
      options.minSliceMs ?? READING_TIME_MIN_SLICE_MS,
      "Minimum reading-time slice",
    );
    this.maxSliceMs = positiveInteger(
      options.maxSliceMs ?? READING_TIME_MAX_SLICE_MS,
      "Maximum reading-time slice",
    );
    if (this.minSliceMs > this.maxSliceMs) {
      throw new RangeError("Minimum reading-time slice cannot exceed the maximum slice.");
    }
  }

  get active(): boolean {
    return this.activeStartedAtMonotonic !== null;
  }

  start(): void {
    if (this.active) return;
    this.activeStartedAtMonotonic = safeClockValue(
      this.clock.monotonicNow(),
      "Reading-time monotonic clock",
    );
    this.activeStartedAtWall = safeClockValue(
      this.clock.wallNow(),
      "Reading-time wall clock",
    );
  }

  resume(): void {
    this.start();
  }

  /** Moves attribution to a new page without losing an in-flight interval. */
  setPage(pageId?: string | null): QueuedReadingTimeEvent[] {
    const nextPageId = pageId ?? null;
    if (nextPageId === this.pageId) return [];
    const wasActive = this.active;
    const events = this.capture(false);
    this.pageId = nextPageId;
    if (wasActive) this.start();
    return events;
  }

  /** Ends the current active interval and returns any recordable slices. */
  pause(): QueuedReadingTimeEvent[] {
    return this.capture(false);
  }

  /** Records elapsed time while leaving the session active. */
  flush(): QueuedReadingTimeEvent[] {
    return this.capture(true);
  }

  heartbeat(): QueuedReadingTimeEvent[] {
    return this.flush();
  }

  private capture(keepActive: boolean): QueuedReadingTimeEvent[] {
    if (
      this.activeStartedAtMonotonic === null ||
      this.activeStartedAtWall === null
    ) {
      return [];
    }

    const capturedAtMonotonic = safeClockValue(
      this.clock.monotonicNow(),
      "Reading-time monotonic clock",
    );
    const capturedAtWall = safeClockValue(
      this.clock.wallNow(),
      "Reading-time wall clock",
    );
    const elapsedMs = Math.floor(
      Math.max(0, capturedAtMonotonic - this.activeStartedAtMonotonic),
    );

    if (!keepActive) {
      this.activeStartedAtMonotonic = null;
      this.activeStartedAtWall = null;
    } else if (elapsedMs >= this.minSliceMs) {
      this.activeStartedAtMonotonic = capturedAtMonotonic;
      this.activeStartedAtWall = capturedAtWall;
    }

    const durations = splitDuration(elapsedMs, this.minSliceMs, this.maxSliceMs);
    if (durations.length === 0) return [];

    const intervalStartedAt = Math.trunc(this.activeStartedAtWall ?? (
      // pause() clears the anchors before event creation, so reconstruct its
      // original wall-clock start from the capture point and elapsed time.
      capturedAtWall - elapsedMs
    ));
    const wallStart = keepActive
      ? Math.trunc(capturedAtWall - elapsedMs)
      : intervalStartedAt;

    let offsetMs = 0;
    return durations.map((activeMs) => {
      const startedAtMs = wallStart + offsetMs;
      const endedAtMs = startedAtMs + activeMs;
      offsetMs += activeMs;
      return {
        bookId: this.bookId,
        ...(this.pageId ? { pageId: this.pageId } : {}),
        eventId: this.eventIdFactory(),
        startedAt: new Date(startedAtMs).toISOString(),
        endedAt: new Date(endedAtMs).toISOString(),
        activeMs,
      };
    });
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

const RETRYABLE_READING_TIME_CLIENT_STATUSES = new Set([
  401, // An expired or absent session can be repaired by signing in.
  403, // Authentication/same-origin state may be refreshed.
  407, // Proxy authentication may be refreshed.
  408, // The request timed out before being accepted.
  421, // A different connection may reach the intended server.
  423, // A temporary server-side lock may clear.
  424, // A transient dependency may recover.
  425, // Retrying later is the prescribed response.
  429, // Respect rate limiting without losing reading time.
]);

/**
 * True only when retrying the unchanged event cannot repair the request.
 * Unknown errors, authentication failures, rate limits, and 5xx responses are
 * deliberately retained for a later replay.
 */
export function isTerminalReadingTimeDeliveryError(error: unknown): boolean {
  if (!isObject(error) || typeof error.status !== "number") return false;
  const status = error.status;
  return Number.isInteger(status) &&
    status >= 400 &&
    status < 500 &&
    !RETRYABLE_READING_TIME_CLIENT_STATUSES.has(status);
}

export function isQueuedReadingTimeEvent(value: unknown): value is QueuedReadingTimeEvent {
  if (!isObject(value)) return false;
  if (typeof value.bookId !== "string" || value.bookId.trim().length === 0) return false;
  if (typeof value.eventId !== "string" || !UUID_PATTERN.test(value.eventId)) return false;
  if (value.pageId !== undefined && value.pageId !== null && (
    typeof value.pageId !== "string" || !UUID_PATTERN.test(value.pageId)
  )) return false;
  if (typeof value.startedAt !== "string" || typeof value.endedAt !== "string") return false;
  if (
    typeof value.activeMs !== "number" ||
    !Number.isInteger(value.activeMs) ||
    value.activeMs < 1 ||
    value.activeMs > READING_TIME_MAX_SLICE_MS
  ) {
    return false;
  }

  const startedAtMs = Date.parse(value.startedAt);
  const endedAtMs = Date.parse(value.endedAt);
  return Number.isFinite(startedAtMs) &&
    Number.isFinite(endedAtMs) &&
    endedAtMs - startedAtMs === value.activeMs;
}

function deduplicateAndBound(
  events: readonly QueuedReadingTimeEvent[],
  maxEvents: number,
): QueuedReadingTimeEvent[] {
  const unique = new Map<string, QueuedReadingTimeEvent>();
  for (const event of events) {
    if (isQueuedReadingTimeEvent(event) && !unique.has(event.eventId)) {
      unique.set(event.eventId, event);
    }
  }
  return [...unique.values()].slice(-maxEvents);
}

function defaultStorage(): ReadingTimeStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Durable FIFO storage for unsent reading-time slices. */
export class ReadingTimeOutbox {
  private readonly storage: ReadingTimeStorage | null;
  private readonly storageKey: string;
  private readonly maxEvents: number;
  private storageAvailable = true;
  private volatileEvents: QueuedReadingTimeEvent[] = [];
  private drainInFlight: Promise<ReadingTimeOutboxDrainResult> | null = null;

  constructor(options: ReadingTimeOutboxOptions = {}) {
    this.storage = options.storage === undefined ? defaultStorage() : options.storage;
    this.storageKey = options.storageKey ?? READING_TIME_OUTBOX_KEY;
    this.maxEvents = positiveInteger(
      options.maxEvents ?? READING_TIME_OUTBOX_MAX_EVENTS,
      "Reading-time outbox limit",
    );
  }

  list(): QueuedReadingTimeEvent[] {
    return deduplicateAndBound(
      [...this.readPersisted(), ...this.volatileEvents],
      this.maxEvents,
    );
  }

  enqueue(events: readonly QueuedReadingTimeEvent[]): QueuedReadingTimeEvent[] {
    if (events.length === 0) return this.list();
    const next = deduplicateAndBound([...this.list(), ...events], this.maxEvents);
    this.replace(next);
    return next;
  }

  acknowledge(eventIds: Iterable<string>): QueuedReadingTimeEvent[] {
    const acknowledged = new Set(eventIds);
    if (acknowledged.size === 0) return this.list();
    const remaining = this.list().filter((event) => !acknowledged.has(event.eventId));
    this.replace(remaining);
    return remaining;
  }

  drain(
    send: SendReadingTimeEvent,
    options: DrainReadingTimeOutboxOptions = {},
  ): Promise<ReadingTimeOutboxDrainResult> {
    if (this.drainInFlight) return this.drainInFlight;

    const drain = this.performDrain(send, options).finally(() => {
      if (this.drainInFlight === drain) this.drainInFlight = null;
    });
    this.drainInFlight = drain;
    return drain;
  }

  private async performDrain(
    send: SendReadingTimeEvent,
    options: DrainReadingTimeOutboxOptions,
  ): Promise<ReadingTimeOutboxDrainResult> {
    const limit = options.maxEvents === undefined
      ? this.maxEvents
      : Math.max(0, Math.floor(options.maxEvents));
    const pending = this.list().slice(0, limit);
    let attempted = 0;
    let sent = 0;
    let failed = false;

    for (const queued of pending) {
      if (options.signal?.aborted) break;
      attempted += 1;
      const { bookId, ...event } = queued;
      try {
        await send(bookId, event, {
          signal: options.signal,
          keepalive: options.keepalive ?? false,
        });
        this.acknowledge([queued.eventId]);
        sent += 1;
      } catch (error) {
        if (isTerminalReadingTimeDeliveryError(error)) {
          // Invalid, deleted-book, and id-conflict events cannot succeed with
          // the same payload. Remove only that poison event and keep draining.
          this.acknowledge([queued.eventId]);
          continue;
        }

        // Retryable failures stay at the front of the queue. Stopping avoids
        // hammering an unavailable or unauthenticated server with the outbox.
        failed = true;
        break;
      }
    }

    return {
      attempted,
      sent,
      remaining: this.list().length,
      failed,
    };
  }

  private readPersisted(): QueuedReadingTimeEvent[] {
    if (!this.storage || !this.storageAvailable) return [];
    let raw: string | null;
    try {
      raw = this.storage.getItem(this.storageKey);
    } catch {
      this.storageAvailable = false;
      return [];
    }
    if (!raw) return [];

    try {
      const parsed: unknown = JSON.parse(raw);
      const events = Array.isArray(parsed)
        ? parsed
        : isObject(parsed) && parsed.version === 1 && Array.isArray(parsed.events)
          ? parsed.events
          : [];
      return deduplicateAndBound(
        events.filter(isQueuedReadingTimeEvent),
        this.maxEvents,
      );
    } catch {
      return [];
    }
  }

  private replace(events: readonly QueuedReadingTimeEvent[]): void {
    const bounded = deduplicateAndBound(events, this.maxEvents);
    if (!this.storage || !this.storageAvailable) {
      this.volatileEvents = bounded;
      return;
    }

    const stored: StoredReadingTimeOutbox = { version: 1, events: bounded };
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(stored));
      this.volatileEvents = [];
    } catch {
      // Restricted/private browsing may expose localStorage but reject writes.
      // Retain the queue in memory so the current session can still retry.
      this.storageAvailable = false;
      this.volatileEvents = bounded;
    }
  }
}

let sharedReadingTimeOutbox: ReadingTimeOutbox | null = null;

/** One browser-wide outbox lets reader cleanup and the returning library await the same replay. */
export function getSharedReadingTimeOutbox(): ReadingTimeOutbox {
  sharedReadingTimeOutbox ??= new ReadingTimeOutbox();
  return sharedReadingTimeOutbox;
}

/** Combines lifecycle capture with write-before-send outbox persistence. */
export class ReadingTimeRecorder {
  readonly session: ReadingTimeSession;
  readonly outbox: ReadingTimeOutbox;

  constructor(bookId: string, options: ReadingTimeRecorderOptions = {}) {
    this.session = new ReadingTimeSession(bookId, options);
    this.outbox = options.outbox ?? new ReadingTimeOutbox(options);
  }

  get active(): boolean {
    return this.session.active;
  }

  start(): void {
    this.session.start();
  }

  resume(): void {
    this.start();
  }

  setPage(pageId?: string | null): QueuedReadingTimeEvent[] {
    return this.captureAndQueue(() => this.session.setPage(pageId));
  }

  pause(): QueuedReadingTimeEvent[] {
    return this.captureAndQueue(() => this.session.pause());
  }

  flush(): QueuedReadingTimeEvent[] {
    return this.captureAndQueue(() => this.session.flush());
  }

  heartbeat(): QueuedReadingTimeEvent[] {
    return this.captureAndQueue(() => this.session.heartbeat());
  }

  replay(
    send: SendReadingTimeEvent,
    options: DrainReadingTimeOutboxOptions = {},
  ): Promise<ReadingTimeOutboxDrainResult> {
    return this.outbox.drain(send, options);
  }

  private captureAndQueue(capture: () => QueuedReadingTimeEvent[]): QueuedReadingTimeEvent[] {
    const events = capture();
    this.outbox.enqueue(events);
    return events;
  }
}
